// EarnHistory.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Divider,
  alpha,
  Paper,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import axios from "axios";
import {
  ASSETICONBASEURL,
  CACHESERVICE,
  WEBSERVICE,
} from "../../../CoreDataService";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import { useSelector } from "react-redux";
import { ContainedButton, OutlinedButton } from "../../../Common/Buttons";

const EarnHistory = () => {
  const { btncolor } = useSelector((state) => state);
  const [tab, setTab] = useState(0);

  const [rows, setRows] = useState([]);
  const [pages, setPages] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  const pageSize = 20;
  const [totalCount, setTotalCount] = useState(0);

  const [eventFilter, setEventFilter] = useState("");
  const [assetFilter, setAssetFilter] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [assets, setAssets] = useState([]);

  const eventList = [
    "Invested by User",
    "Auto-Invested by Platform",
    "Earned Interest",
    "Turned off Auto-invest",
    "Turned on Auto-invest",
    "Auto-moved to Flexible Earnings",
    "Redeemed",
    "Lock-in Expired",
  ];
  const BROKERID = localStorage.getItem("BROKERID");

  const fetchData = useCallback(
    async (isFlexible, pn = 1) => {
      try {
        const payload = {
          uuid: localStorage.getItem("uuid"),
          eventType: eventFilter,
          currencyId: assetFilter,
          fromDate: fromDate ? fromDate.toISOString().split("T")[0] : "",
          toDate: toDate ? toDate.toISOString().split("T")[0] : "",
          isFlexible: isFlexible,
          pageNo: pn,
          pageSize,
        };
        const res = await axios.post(
          `${WEBSERVICE}crypto/banking/earn/getEarnInterestHistoriesByUser`,
          payload,
          {
            headers: {
              authorization: `BEARER ${localStorage.getItem("access_token")}`,
            },
          }
        );
        if (res.data.error.error_data !== 0) {
          throw new Error(res.data.error.error_msg);
        }
        const list = res.data.investmentHistories;
        setPages(
          Array.from(
            { length: Math.ceil(res.data.totalCount / pageSize) },
            (_, i) => i + 1
          )
        );
        setTotalCount(res.data.totalCount);
        setRows(list || []);
        setPageNo(pn);
      } catch (err) {
        console.error(err);
      }
    },
    [eventFilter, assetFilter, fromDate, toDate]
  );

  const downloadReport = async (isFlexible) => {
    try {
      const payload = {
        uuid: localStorage.getItem("uuid"),
        eventType: eventFilter,
        currencyId: assetFilter,
        fromDate: fromDate ? fromDate.toISOString().split("T")[0] : "",
        toDate: toDate ? toDate.toISOString().split("T")[0] : "",
        isFlexible: isFlexible,
        pageNo,
        pageSize,
      };
      const res = await axios.post(
        `${WEBSERVICE}crypto/banking/earn/getEarnInterestHistoriesReportByUser`,
        payload,
        {
          headers: {
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", `earn_history_${Date.now()}.xlsx`);
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error(err);
    }
  };

  const getAllAsset = async () => {
    try {
      const response = await fetch(
        `${CACHESERVICE}/getAssetsData?Name=Assets&BrokerId=${BROKERID}`
      );
      const responseCurrency = await response.json();
      const parsedData = JSON.parse(responseCurrency.value);
      const filterCurrency = parsedData.Values;
      const uniqueAssets = [];
      for (let i = 0; i < filterCurrency.length; i++) {
        const exists = uniqueAssets.find(
          (x) => x.currencyCode === filterCurrency[i].currencyCode
        );
        if (!exists) {
          uniqueAssets.push(filterCurrency[i]);
        }
      }
      setAssets(uniqueAssets);
    } catch (error) {
      console.error("Failed to fetch asset data:", error);
    }
  };

  useEffect(() => {
    getAllAsset();
    fetchData(tab === 0 ? 1 : 0);
  }, [tab, fetchData]);

  const handleTab = (event, val) => setTab(val);

  const tableColumns = [
    "Investment ID",
    "Timestamp",
    "Event",
    "Asset",
    "Amount",
    "Lock-in Period",
    "Investment Schedule",
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={pageWrapper}>

        {/* ── Hero Banner ── */}
        <Box
          sx={{
            ...heroBanner,
            background: `linear-gradient(135deg, ${alpha(btncolor || "#1976d2", 0.95)} 0%, ${alpha(btncolor || "#1976d2", 0.75)} 100%)`,
          }}
        >
          <Box sx={heroContent}>
            <Box sx={heroIconBadge}>
              <HistoryOutlinedIcon sx={{ color: "#fff", fontSize: 30 }} />
            </Box>
            <Box>
              <Typography sx={heroTitle}>Earn History</Typography>
              <Typography sx={heroSubtitle}>
                Track your flexible and lock-in earn activity, interest events and investment schedules
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* ── Main Card ── */}
        <Box sx={mainCard}>

          {/* ── Tabs ── */}
          <Box sx={tabsWrap}>
            <Tabs
              value={tab}
              onChange={handleTab}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                ...tabsStyle,
                "& .MuiTabs-indicator": {
                  backgroundColor: btncolor,
                  height: 3,
                  borderRadius: "3px 3px 0 0",
                },
                "& .Mui-selected": { color: `${btncolor} !important` },
              }}
            >
              <Tab label="Flexible" sx={tabItem} />
              <Tab label="Lock-in Period" sx={tabItem} />
            </Tabs>
          </Box>

          <Box sx={tabPanel}>

            {/* ── Filter Card ── */}
            <Box sx={filterCard}>
              <Box sx={filterHeader}>
                <FilterAltOutlinedIcon sx={{ ...filterHeaderIcon, color: btncolor }} />
                <Typography sx={filterHeaderText}>Filter History</Typography>
              </Box>

              <Box sx={filterGrid}>
                {/* Event filter */}
                <Box sx={fieldWrap}>
                  <Typography sx={fieldLabel}>Event Type</Typography>
                  <TextField
                    select
                    fullWidth
                    value={eventFilter}
                    onChange={(e) => setEventFilter(e.target.value)}
                    sx={modernFieldStyle}
                  >
                    <MenuItem value="">All Events</MenuItem>
                    {eventList.map((e, i) => (
                      <MenuItem key={i} value={e}>{e}</MenuItem>
                    ))}
                  </TextField>
                </Box>

                {/* Asset filter */}
                <Box sx={fieldWrap}>
                  <Typography sx={fieldLabel}>Asset</Typography>
                  <TextField
                    select
                    fullWidth
                    value={assetFilter}
                    onChange={(e) => setAssetFilter(e.target.value)}
                    sx={modernFieldStyle}
                  >
                    <MenuItem value="">All Assets</MenuItem>
                    {assets.map((a) => (
                      <MenuItem key={a.currencyId} value={a.currencyId}>
                        {a.currencyCode}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                {/* From Date */}
                <Box sx={fieldWrap}>
                  <Typography sx={fieldLabel}>
                    <EventOutlinedIcon sx={fieldLabelIcon} /> From Date
                  </Typography>
                  <DatePicker
                    value={fromDate}
                    onChange={setFromDate}
                    maxDate={toDate || undefined}
                    slotProps={{
                      textField: { sx: modernFieldStyle, fullWidth: true },
                    }}
                  />
                </Box>

                {/* To Date */}
                <Box sx={fieldWrap}>
                  <Typography sx={fieldLabel}>
                    <EventOutlinedIcon sx={fieldLabelIcon} /> To Date
                  </Typography>
                  <DatePicker
                    value={toDate}
                    onChange={setToDate}
                    minDate={fromDate || undefined}
                    slotProps={{
                      textField: { sx: modernFieldStyle, fullWidth: true },
                    }}
                  />
                </Box>

                {/* Actions */}
                <Box sx={actionsWrap}>
                  <Tooltip title="Search" arrow>
                    <IconButton
                      onClick={() => fetchData(tab === 0 ? 1 : 0)}
                      sx={{
                        ...actionIconBtn,
                        backgroundColor: btncolor,
                        "&:hover": { backgroundColor: btncolor, opacity: 0.9 },
                      }}
                    >
                      <SearchIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Export Excel" arrow>
                    <IconButton
                      onClick={() => downloadReport(tab === 0 ? 1 : 0)}
                      sx={{
                        ...actionIconBtn,
                        backgroundColor: btncolor,
                        "&:hover": { backgroundColor: btncolor, opacity: 0.9 },
                      }}
                    >
                      <FileDownloadOutlinedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>

            {/* ── Results block ── */}
            <Box sx={resultsBlock}>
              <Box sx={resultsHeader}>
                <Box>
                  <Typography sx={resultsTitle}>
                    {tab === 0 ? "Flexible" : "Lock-in Period"} History
                  </Typography>
                  <Typography sx={resultsCaption}>
                    {totalCount > 0
                      ? `Showing ${rows.length} of ${totalCount} entries`
                      : "No records found"}
                  </Typography>
                </Box>
              </Box>

              <TableContainer component={Paper} sx={modernTableContainer}>
                <Table sx={tableStyle}>
                  <TableHead>
                    <TableRow sx={modernTableHeadRow}>
                      {tableColumns.map((col, idx) => (
                        <TableCell key={idx} sx={modernTableHeadCell}>
                          {col}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length > 0 ? (
                      rows.map((r, idx) => (
                        <TableRow key={idx} sx={modernTableBodyRow}>
                          <TableCell sx={modernTableBodyCell}>{r.investmentId}</TableCell>
                          <TableCell sx={modernTableBodyCell}>{r.createdOn}</TableCell>
                          <TableCell sx={modernTableBodyCell}>{r.action}</TableCell>
                          <TableCell sx={modernTableBodyCell}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <Tooltip title={r.currency}>
                                <img
                                  src={`${ASSETICONBASEURL}/assets/icons/${r.currency}.png`}
                                  alt=""
                                  width="20"
                                  style={{ borderRadius: "50%", flexShrink: 0 }}
                                />
                              </Tooltip>
                              {r.currency}
                            </Box>
                          </TableCell>
                          <TableCell sx={modernTableBodyCell}>{r.amount}</TableCell>
                          <TableCell sx={modernTableBodyCell}>
                            {r.lockInPeriod}{tab === 0 ? "" : " Days"}
                          </TableCell>
                          <TableCell sx={modernTableBodyCell}>{r.investmentSchedule}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={tableColumns.length} sx={emptyCell}>
                          <InboxOutlinedIcon sx={emptyIcon} />
                          <Typography sx={emptyText}>No history records found</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* ── Pagination ── */}
              {pages.length > 1 && (
                <Box sx={paginationWrap}>
                  <Pagination
                    count={pages.length}
                    page={pageNo}
                    boundaryCount={2}
                    onChange={(event, value) => fetchData(tab === 0 ? 1 : 0, value)}
                    sx={{
                      "& .MuiPaginationItem-root": {
                        color: "var(--text-color)",
                        borderColor: "rgba(125,125,125,0.25)",
                        borderRadius: "8px",
                        margin: "0 2px",
                      },
                      "& .MuiPaginationItem-root.Mui-selected": {
                        backgroundColor: btncolor,
                        color: "#fff",
                        borderColor: btncolor,
                      },
                      "& .MuiPaginationItem-root:hover": {
                        backgroundColor: alpha(btncolor || "#1976d2", 0.15),
                      },
                      "& .MuiPaginationItem-ellipsis": {
                        color: "var(--text-color)",
                        opacity: 0.5,
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Auto-Invest Info Modal ── */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        PaperProps={{ sx: dialogPaper }}
      >
        <DialogTitle sx={dialogTitleStyle}>Auto Invest</DialogTitle>
        <Divider />
        <DialogContent sx={dialogContentStyle}>
          <Typography sx={dialogText}>
            What happens when this auto-invest feature is turned off?
          </Typography>
          <Typography sx={{ ...dialogText, mt: 1.5 }}>
            (1) It will stop auto-investing for you.
          </Typography>
          <Typography sx={{ ...dialogText, mt: 0.75 }}>
            (2) Your already invested assets will remain in the Earn wallet and continue to earn interest.
          </Typography>
        </DialogContent>
        <DialogActions sx={dialogActionsStyle}>
          <ContainedButton
            variant="contained"
            sx={{ mx: 1, minWidth: 90 }}
            onClick={() => {
              /* TODO: generatePayloadForNoAutoInvest logic */
              setModalOpen(false);
            }}
          >
            Ok
          </ContainedButton>
          <OutlinedButton
            variant="outlined"
            sx={{ mx: 1, minWidth: 90 }}
            onClick={() => setModalOpen(false)}
          >
            Cancel
          </OutlinedButton>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

// ── Styles (mirrors Report.js / RecurringPayment.js conventions) ──────────────

const pageWrapper = {
  px: { xs: 2, md: 4 },
  py: { xs: 2, md: 3 },
  backgroundColor: "var(--body-color)",
  color: "var(--text-color)",
  minHeight: "calc(100vh - 64px)",
};

const heroBanner = {
  borderRadius: "18px",
  px: { xs: 3, md: 4 },
  py: { xs: 3, md: 3.5 },
  mb: 3,
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
};

const heroContent = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  position: "relative",
  zIndex: 1,
};

const heroIconBadge = {
  width: 56,
  height: 56,
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.25)",
  backdropFilter: "blur(6px)",
};

const heroTitle = {
  fontSize: { xs: "1.4rem", md: "1.7rem" },
  fontWeight: 700,
  color: "#fff",
  lineHeight: 1.15,
  letterSpacing: "-0.015em",
};

const heroSubtitle = {
  fontSize: { xs: "0.82rem", md: "0.9rem" },
  color: "rgba(255,255,255,0.88)",
  mt: 0.5,
  maxWidth: 640,
};

const mainCard = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const tabsWrap = {
  borderBottom: "1px solid rgba(125,125,125,0.15)",
  px: { xs: 1, md: 2 },
  backgroundColor: "var(--body-color-less2)",
};

const tabsStyle = {
  minHeight: 56,
  "& .MuiTab-root": { minHeight: 56 },
};

const tabItem = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.9rem",
  color: "var(--text-color)",
  opacity: 0.65,
  letterSpacing: "0.01em",
  px: 2,
  "&.Mui-selected": { color: "var(--text-color)", opacity: 1 },
};

const tabPanel = {
  p: { xs: 2, md: 3 },
};

const filterCard = {
  borderRadius: "12px",
  p: { xs: 2, md: 2.5 },
  mb: 3,
};

const filterHeader = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  mb: 2,
};

const filterHeaderIcon = {
  fontSize: 18,
};

const filterHeaderText = {
  fontSize: "0.85rem",
  fontWeight: 600,
  color: "var(--text-color)",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};

const filterGrid = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr auto" },
  gap: 2,
  alignItems: "end",
};

const fieldWrap = {
  display: "flex",
  flexDirection: "column",
  gap: 0.75,
  minWidth: 0,
};

const fieldLabel = {
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "var(--text-color)",
  opacity: 0.7,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  display: "flex",
  alignItems: "center",
  gap: 0.5,
};

const fieldLabelIcon = {
  fontSize: 14,
  color: "var(--text-color)",
  opacity: 0.7,
};

const modernFieldStyle = {
  width: "100%",
  "& .MuiInputBase-root": {
    height: "48px",
    borderRadius: "10px",
    backgroundColor: "var(--body-color)",
    color: "var(--text-color)",
    fontSize: "0.9rem",
  },
  "& .MuiInputBase-input": { color: "var(--text-color)" },
  "& .MuiSelect-select": { color: "var(--text-color)" },
  "& .MuiSvgIcon-root": { color: "var(--text-color)", opacity: 0.7 },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(125,125,125,0.25)",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(125,125,125,0.45)",
  },
  "& .MuiInputLabel-root": { display: "none" },
  "& legend": { display: "none" },
  "& fieldset": { top: 0 },
};

const actionsWrap = {
  display: "flex",
  alignItems: "flex-end",
  gap: 1,
  pb: "1px",
};

const actionIconBtn = {
  width: 48,
  height: 48,
  borderRadius: "10px",
  color: "#fff",
  flexShrink: 0,
};

const resultsBlock = {
  mt: 1,
};

const resultsHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 1.5,
  mb: 2,
};

const resultsTitle = {
  fontSize: "1rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.2,
};

const resultsCaption = {
  fontSize: "0.78rem",
  color: "var(--text-color)",
  opacity: 0.65,
  mt: 0.25,
};

const modernTableContainer = {
  borderRadius: "12px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "none",
  overflow: "hidden",
  backgroundColor: "var(--body-color-less)",
};

const tableStyle = {
  minWidth: 750,
};

const modernTableHeadRow = {
  backgroundColor: "var(--body-color-less2)",
};

const modernTableHeadCell = {
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--text-color)",
  opacity: 0.75,
  borderBottom: "1px solid rgba(125,125,125,0.15)",
  py: 1.75,
  whiteSpace: "nowrap",
};

const modernTableBodyRow = {
  transition: "background-color 0.15s ease",
  "&:hover": { backgroundColor: "var(--body-color-less2)" },
  "&:last-child td": { borderBottom: 0 },
};

const modernTableBodyCell = {
  fontSize: "0.85rem",
  py: 1.65,
  borderBottom: "1px solid rgba(125,125,125,0.12)",
  color: "var(--text-color)",
  whiteSpace: "nowrap",
};

const emptyCell = {
  textAlign: "center",
  py: 7,
  borderBottom: 0,
};

const emptyIcon = {
  fontSize: 48,
  color: "var(--text-color)",
  opacity: 0.35,
  mb: 1,
};

const emptyText = {
  fontSize: "0.92rem",
  color: "var(--text-color)",
  opacity: 0.65,
};

const paginationWrap = {
  display: "flex",
  justifyContent: "flex-end",
  pt: 2,
  borderTop: "1px solid rgba(125,125,125,0.15)",
  mt: 0,
  backgroundColor: "var(--body-color-less2)",
  px: 2,
  pb: 1.5,
  borderRadius: "0 0 12px 12px",
};

const dialogPaper = {
  borderRadius: "16px",
  minWidth: { xs: "85%", sm: 440 },
  boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
  overflow: "hidden",
  backgroundColor: "var(--popupbody-color)",
  color: "var(--text-color)",
};

const dialogTitleStyle = {
  textAlign: "center",
  fontWeight: 700,
  fontSize: "1.15rem",
  py: 2.25,
  color: "var(--text-color)",
  backgroundColor: "var(--popupheader-color)",
};

const dialogContentStyle = {
  pt: 3,
  pb: 2,
  px: 3,
  backgroundColor: "var(--popupbody-color)",
};

const dialogText = {
  fontSize: "0.9rem",
  color: "var(--text-color)",
  opacity: 0.85,
  lineHeight: 1.6,
};

const dialogActionsStyle = {
  justifyContent: "center",
  pb: 2.5,
  px: 3,
  backgroundColor: "var(--popupbody-color)",
};

export default EarnHistory;
